import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI, regionsAPI } from '../../api';
import { useLang } from '../../contexts/LangContext';
import { t } from '../../i18n/translations';
import ReadingsTable from './ReadingsTable';
import { getPrice, getBasePrice, getVatRate } from '../../utils/pricing'; // ✅ سعر موحّد شامل الضريبة (מע"מ)
import { cupsPositive } from '../../utils/cups'; // ✅ فرق أكواب موحّد (يدعم تبديل العداد ضمن نفس الفترة)
import { getExtrasNet, getExtrasGross, groupExtrasByLand } from '../../utils/extras'; // ✅ إضافات تابعة للأرض (LandExtra) — المبلغ المُدخل هو المبلغ النهائي مباشرة (بدون فصل قبل/بعد ضريبة)
const dmsToDecimal = (deg, min, sec, dir) => {
  let dd = parseFloat(deg) + parseFloat(min) / 60 + parseFloat(sec) / 3600;
  if (/[SW]/i.test(dir)) dd = -dd;
  return parseFloat(dd.toFixed(6));
};

const parseGoogleCoords = (raw) => {
  if (!raw || raw.trim().length < 3) return null;
  const s = raw.trim();
  const decMatch = s.match(/^(-?\d{1,3}\.\d+)[,\s]+(-?\d{1,3}\.\d+)$/);
  if (decMatch) return { lat: parseFloat(decMatch[1]), lng: parseFloat(decMatch[2]) };
  const dmsPattern = /(\d{1,3})[°°]\s*(\d{1,2})['\'′]\s*(\d{1,2}(?:\.\d+)?)["\"″]?\s*([NS])/i;
  const dmsPattern2 = /(\d{1,3})[°°]\s*(\d{1,2})['\'′]\s*(\d{1,2}(?:\.\d+)?)["\"″]?\s*([EW])/i;
  const latM = s.match(dmsPattern);
  const lngM = s.match(dmsPattern2);
  if (latM && lngM) return {
    lat: dmsToDecimal(latM[1], latM[2], latM[3], latM[4]),
    lng: dmsToDecimal(lngM[1], lngM[2], lngM[3], lngM[4]),
  };
  return null;
};

const EMPTY_FORM = {
  farmerId: '', landId: '', year: new Date().getFullYear(),
  readings: ['', ''],
  // ✅ الإضافات ما عادت جزء من نموذج القراءة — صارت تابعة للأرض (landId) وتُدار
  // مباشرة (تُحفظ فوراً بالسيرفر) بدل ما تنحفظ مع "حفظ" القراءة كاملة
  // ✅ تبديلات العداد: [{ period, oldFinal, newInitial }] — period = فهرس الفترة (0-based)
  // بين readings[period] و readings[period+1]. الاستهلاك يُدمج بنفس رقم هذه الفترة.
  meterChanges: [],
};

// ✅ مكوّن إضافة واحدة — الآن مربوطة مباشرة بالأرض (LandExtra) وتُحفظ فوراً
// بالسيرفر عند فقدان التركيز (blur)، مش عند "حفظ" القراءة
// ✅ الاسم يُختار من مخزن الإضافات (catalog) أو يُكتب حراً — عند الاختيار من المخزن
// يتعبّى السعر المقترح تلقائياً ويُربط catalogItemId. تعديل السعر بعدها يعتبر "تغيير
// لمرة واحدة" (خاص بهذه الإضافة فقط) إلا لو ضغط المستخدم "تحديث سعر المخزن" صراحة —
// عندها يتحدّث السعر الافتراضي بالمخزن نفسه بشكل دائم. قبل/بعد الضريبة هون للعرض فقط
// (حيّة من نسبة الضريبة العامة الحالية) ولا تؤثر على amount/paid الفعليين إطلاقاً.
function ExtraRow({ extra, onSave, onRemove, catalog, prices, ar, onUpdateCatalogPrice }) {
  const [showAc, setShowAc] = useState(false);
  const [draft, setDraft] = useState({
    note: extra.note || '', amount: String(extra.amount ?? ''), paid: String(extra.paid ?? ''),
    catalogItemId: extra.catalogItemId || null,
  });
  const [saving, setSaving] = useState(false);
  const [updatingCatalog, setUpdatingCatalog] = useState(false);

  // ✅ لو تغيّر السجل من مصدر خارجي (إعادة تحميل مثلاً)، نزامن الحقول المحلية
  useEffect(() => {
    setDraft({
      note: extra.note || '', amount: String(extra.amount ?? ''), paid: String(extra.paid ?? ''),
      catalogItemId: extra.catalogItemId || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extra.id]);

  const activeCatalog = (catalog || []).filter(c => c.active !== false);
  const filtered = activeCatalog.filter(c => c.name.toLowerCase().includes((draft.note || '').toLowerCase()) && c.name !== draft.note);

  const commit = async (patch) => {
    const merged = { ...draft, ...patch };
    setDraft(merged);
    setSaving(true);
    try {
      await onSave({
        note: merged.note,
        amount: parseFloat(merged.amount) || 0,
        paid: parseFloat(merged.paid) || 0,
        catalogItemId: merged.catalogItemId || null,
      });
    } finally { setSaving(false); }
  };

  // ✅ اختيار عنصر من مخزن الإضافات — يعبّي الاسم والسعر المقترح تلقائياً ويربط catalogItemId
  const pickCatalogItem = (item) => {
    commit({ note: item.name, amount: String(item.defaultPrice ?? 0), catalogItemId: item.id });
  };

  // ✅ العنصر المرتبط حالياً بهذه الإضافة (إن وُجد) — لمقارنة السعر الحالي بسعر المخزن الافتراضي
  const linkedCatalogItem = draft.catalogItemId ? (catalog || []).find(c => c.id === draft.catalogItemId) : null;
  const priceDivergesFromCatalog = linkedCatalogItem && (parseFloat(draft.amount) || 0) !== (linkedCatalogItem.defaultPrice || 0);

  const pushPriceToCatalog = async () => {
    if (!linkedCatalogItem) return;
    setUpdatingCatalog(true);
    try { await onUpdateCatalogPrice(linkedCatalogItem.id, parseFloat(draft.amount) || 0); }
    finally { setUpdatingCatalog(false); }
  };

  // ✅ صيغة تاريخ موحّدة بالمشروع (أرقام لاتينية، تقويم ميلادي) — نفس القاعدة المتّبعة بكل مكان آخر
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(ar ? 'ar-EG-u-nu-latn' : 'he-IL') : '';
  // ✅ المبلغ المُدخل هنا هو المبلغ النهائي (بعد الضريبة) مباشرة — بدون أي فصل قبل/بعد
  const amountVal = parseFloat(draft.amount) || 0;
  // ✅ السعر قبل الضريبة — لا يُعرض كسطر دائم، فقط كـ tooltip (title) يظهر عند تمرير
  // المؤشر فوق حقل المبلغ ويختفي تلقائياً، بدل ما يحسبه المستخدم يدوياً كل مرة
  const beforeTaxHint = amountVal > 0 ? amountVal / (1 + getVatRate(prices)) : 0;
  const amountTitle = amountVal > 0
    ? (ar ? `قبل الضريبة: ₪${Math.round(beforeTaxHint).toLocaleString()}` : `לפני מע"מ: ₪${Math.round(beforeTaxHint).toLocaleString()}`)
    : undefined;

  // ✅ إضافة من نوع "غرامة تأخير" (أُنشئت تلقائياً عبر شباك تطبيق غرامة التأخير) — تُميَّز
  // بصرياً فقط (لون مختلف + شارة)، وباقي السلوك (تعديل/حذف/حفظ تلقائي) يبقى كما هو تماماً
  const isPenalty = extra.kind === 'penalty';

  return (
    <div style={{
      background: isPenalty ? '#fef2f2' : '#fff7ed',
      border: isPenalty ? '1.5px solid #fca5a5' : '1.5px solid #fed7aa',
      borderRadius: 10, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8, position: 'relative',
    }}>
      {isPenalty && (
        <div style={{
          alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, color: '#991b1b',
          background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '2px 8px',
        }}>
          ⏰ {ar ? 'غرامة تأخير' : 'קנס איחור'}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* اسم الإضافة — يُكتب حر أو يُختار من مخزن الإضافات */}
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            value={draft.note}
            onChange={e => setDraft({ ...draft, note: e.target.value, catalogItemId: null })}
            onFocus={() => setShowAc(true)}
            onBlur={() => { setTimeout(() => setShowAc(false), 150); commit({}); }}
            placeholder={ar ? 'سبب الإضافة... (أو اختر من المخزن)' : 'סיבת התוספת... (או בחר מהמלאי)'}
            style={{ width: '100%', fontSize: 13 }}
          />
          {/* قائمة الاقتراحات — من مخزن الإضافات (اسم + سعره الافتراضي) */}
          {showAc && filtered.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200, background: '#fff', border: '1.5px solid #fed7aa', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '100%', maxHeight: 200, overflowY: 'auto' }}>
              {filtered.map(c => (
                <div key={c.id}
                  onMouseDown={() => pickCatalogItem(c)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#92400e', display: 'flex', justifyContent: 'space-between', gap: 10 }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fff7ed'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <span>{c.name}</span>
                  <span style={{ color: '#b45309', fontWeight: 800 }}>₪{c.defaultPrice.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {saving && <span style={{ fontSize: 11, color: '#92400e', flexShrink: 0 }}>⏳</span>}
        <button type="button" onClick={onRemove}
          style={{ width: 26, height: 26, borderRadius: 6, border: '1.5px solid #fca5a5', background: '#fff1f2', color: '#dc2626', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>✕</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={{ fontSize: 11, color: '#92400e', fontWeight: 700, display: 'block', marginBottom: 3 }}>₪ {ar ? 'المبلغ' : 'סכום'} *</label>
          <input type="number" min="0" step="any"
            value={draft.amount}
            onChange={e => setDraft({ ...draft, amount: e.target.value })}
            onBlur={() => commit({})}
            placeholder="0"
            title={amountTitle}
            style={{ width: '100%', fontSize: 15, fontWeight: 700, textAlign: 'center' }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, display: 'block', marginBottom: 3 }}>✅ {ar ? 'المدفوع' : 'שולם'}</label>
          <input type="number" min="0" step="any"
            value={draft.paid}
            onChange={e => setDraft({ ...draft, paid: e.target.value })}
            onBlur={() => commit({})}
            placeholder="0"
            style={{ width: '100%', fontSize: 15, fontWeight: 700, textAlign: 'center' }}
          />
        </div>
      </div>

      {/* ✅ تحديث سعر المخزن بشكل دائم — يظهر فقط لما المبلغ الحالي يختلف عن سعر المخزن المرتبط */}
      {priceDivergesFromCatalog && (
        <button type="button" onClick={pushPriceToCatalog} disabled={updatingCatalog}
          style={{ alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, cursor: 'pointer', border: '1.5px solid #b45309', background: '#fffbeb', color: '#92400e' }}>
          {updatingCatalog ? '⏳' : '🔄'} {ar
            ? `تحديث سعر "${linkedCatalogItem.name}" بالمخزن إلى ₪${amountVal.toLocaleString()} بشكل دائم`
            : `עדכן מחיר "${linkedCatalogItem.name}" במלאי ל-₪${amountVal.toLocaleString()} לצמיתות`}
        </button>
      )}

      {/* شريط التقدم */}
      {parseFloat(draft.amount) > 0 && (
        <div>
          <div style={{ height: 4, borderRadius: 2, background: '#fed7aa', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 2, background: '#16a34a', width: `${Math.min(100, parseFloat(draft.paid || 0) / parseFloat(draft.amount) * 100)}%`, transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: 11, color: parseFloat(draft.paid || 0) >= parseFloat(draft.amount) ? '#16a34a' : '#dc2626', fontWeight: 700, marginTop: 2, textAlign: 'center' }}>
            {parseFloat(draft.paid || 0) >= parseFloat(draft.amount)
              ? (ar ? '✅ مدفوعة كاملاً' : '✅ שולם במלואו')
              : `${ar ? 'متبقي' : 'נותר'}: ₪${(parseFloat(draft.amount) - parseFloat(draft.paid || 0)).toLocaleString()}`}
          </div>
        </div>
      )}

      {/* ✅ تاريخ الإضافة / آخر تعديل — معلوماتي فقط (موجودان أصلاً بالسجل createdAt/updatedAt) */}
      <div style={{ fontSize: 10, color: '#b45309', opacity: 0.8, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {extra.createdAt && <span>📅 {ar ? 'أُضيفت' : 'נוסף'}: {fmtDate(extra.createdAt)}</span>}
        {extra.updatedAt && extra.updatedAt !== extra.createdAt && <span>✏️ {ar ? 'آخر تعديل' : 'עודכן'}: {fmtDate(extra.updatedAt)}</span>}
      </div>
    </div>
  );
}

// ✅ شباك الإضافة الجماعية على مجموعة كبيرة من المزارعين/الأراضي دفعة واحدة
// ✅ بعد نقل الإضافات لتصير تابعة للأرض: ما عاد فيه شرط "لازم يكون في قراءة
// مسجّلة عالأرض" — تقدر تضيف على أي أرض مباشرة بغض النظر عن وجود قراءات لها
function BulkExtraModal({ farmers, lands, regions, readings, onClose, onApplied, ar }) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [paidFully, setPaidFully] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({}); // key: farmerId_landId -> true
  const [excluded, setExcluded] = useState({}); // key -> true (أُزيل يدوياً من المعاينة)
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // {success, fail}

  // ✅ لتفادي إغلاق النافذة عند السحب (تحديد نص) من الداخل للخارج
  const backdropMouseDown = useRef(false);

  const currentYear = new Date().getFullYear();

  // ✅ checkbox ثابت الحجم صراحة — حتى لا يتأثر بستايل input العام بالمشروع
  const cbStyle = { width: 16, height: 16, minWidth: 16, flexShrink: 0, accentColor: '#7c3aed', cursor: 'pointer' };

  const farmerName = id => {
    const f = farmers.find(x => String(x.id) === String(id));
    if (!f) return '—';
    return `${f.lastName || ''} ${f.firstName || ''}`.trim() || f.nameHeb || f.name || '—';
  };

  // ✅ اسم الأرض/المنطقة المعروض بجانب رقم المحطة
  const landLabel = (land) => {
    if (!land) return '—';
    if (land.regionId) {
      const reg = regions.find(r => String(r.id) === String(land.regionId));
      if (reg?.nameHeb && reg.nameHeb !== reg.name) return reg.nameHeb;
      if (reg?.name) return reg.name;
    }
    return land.description || '—';
  };

  // ✅ إجمالي الأكواب المصروفة لهذه الأرض بالسنة الحالية (بيساعد باتخاذ القرار)
  const cupsThisYear = (farmerId, landId) => {
    const r = readings.find(x => String(x.farmerId) === String(farmerId) && String(x.landId) === String(landId) && x.year === currentYear);
    if (!r) return null;
    const vals = r.readings || [];
    const changes = r.meterChanges || [];
    let total = 0;
    vals.slice(1).forEach((_, i) => { total += cupsPositive(vals, i, changes); });
    return total;
  };

  const farmersWithLands = farmers
    .map(f => ({ farmer: f, farmerLands: lands.filter(l => String(l.farmerId) === String(f.id)) }))
    .filter(x => x.farmerLands.length > 0)
    .sort((a, b) => (a.farmer.nameHeb || a.farmer.name || '').localeCompare(b.farmer.nameHeb || b.farmer.name || '', 'ar'));

  const filteredFarmers = farmersWithLands.filter(x => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (x.farmer.nameHeb || x.farmer.name || '').toLowerCase().includes(q);
  });

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleLand = (key) => setSelected(prev => ({ ...prev, [key]: !prev[key] }));

  const toggleFarmerAll = (farmerLands, checked) => {
    setSelected(prev => {
      const next = { ...prev };
      farmerLands.forEach(l => { next[`${l.farmerId}_${l.id}`] = checked; });
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(prev => {
      const next = { ...prev };
      filteredFarmers.forEach(({ farmerLands }) => {
        farmerLands.forEach(l => { next[`${l.farmerId}_${l.id}`] = true; });
      });
      return next;
    });
  };

  const clearAll = () => setSelected({});

  // ✅ كل صف مُحدَّد = مؤهّل للتطبيق مباشرة (ما عاد يعتمد على وجود قراءة مسجّلة)
  const applyRows = Object.keys(selected).filter(k => selected[k] && !excluded[k]).map(key => {
    const [farmerId, landId] = key.split('_');
    const land = lands.find(l => String(l.id) === String(landId));
    return { key, farmerId, landId, land };
  });

  const applyBulk = async () => {
    if (applyRows.length === 0 || !title.trim() || !amount) return;
    if (!window.confirm(
      ar
        ? `تطبيق "${title.trim()}" (₪${amount}) على ${applyRows.length} أرض؟`
        : `להחיל "${title.trim()}" (₪${amount}) על ${applyRows.length} קרקעות?`
    )) return;
    setApplying(true); setProgress(0);
    let success = 0, fail = 0;
    for (const row of applyRows) {
      try {
        await adminAPI.createLandExtra({
          landId: row.landId,
          note: title.trim(),
          amount: parseFloat(amount) || 0,
          paid: paidFully ? (parseFloat(amount) || 0) : 0,
        });
        success++;
      } catch (e) { console.error(e); fail++; }
      setProgress(p => p + 1);
    }
    setApplying(false);
    setResult({ success, fail });
    onApplied();
  };

  return (
    <div
      onMouseDown={(e) => { backdropMouseDown.current = (e.target === e.currentTarget); }}
      onClick={(e) => {
        if (backdropMouseDown.current && e.target === e.currentTarget && !applying) onClose();
        backdropMouseDown.current = false;
      }}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>

        {/* الرأس */}
        <div style={{ padding: '14px 20px', background: '#4c1d95', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>👥</span>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>
              {ar ? 'إضافة جماعية على مزارعين' : 'הוספה קבוצתית לחקלאים'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#ddd6fe', fontSize: 12 }}>{ar ? `الخطوة ${step} من 3` : `שלב ${step} מתוך 3`}</span>
            {!applying && (
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 26, height: 26, borderRadius: '50%', cursor: 'pointer' }}>✕</button>
            )}
          </div>
        </div>

        {/* شريط التقدّم */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0' }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{ height: 4, flex: 1, borderRadius: 4, background: n <= step ? '#7c3aed' : '#e5e7eb' }} />
          ))}
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>

          {/* ── خطوة 1: بيانات الإضافة ── */}
          {step === 1 && (
            <div>
              <div className="form-group">
                <label>{ar ? 'عنوان الإضافة *' : 'כותרת התוספת *'}</label>
                <input value={title} onChange={e => setTitle(e.target.value)}
                  placeholder={ar ? 'مثال: اشتراك خط مياه جديد' : 'לדוג׳: מנוי קו מים חדש'} style={{ width: '100%' }} />
              </div>
              <div className="form-group">
                <label>₪ {ar ? 'المبلغ *' : 'סכום *'}</label>
                <input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="400" style={{ width: '100%', fontWeight: 700 }} />
              </div>
              <div className="form-group">
                <label>{ar ? 'حالة الدفع' : 'סטטוס תשלום'}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setPaidFully(true)}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                      border: `2px solid ${paidFully ? '#16a34a' : '#d1d5db'}`,
                      background: paidFully ? '#f0fdf4' : '#fff', color: paidFully ? '#16a34a' : 'var(--text-muted)'
                    }}>
                    ✓ {ar ? 'تم الدفع' : 'שולם'}
                  </button>
                  <button type="button" onClick={() => setPaidFully(false)}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                      border: `2px solid ${!paidFully ? '#dc2626' : '#d1d5db'}`,
                      background: !paidFully ? '#fff1f2' : '#fff', color: !paidFully ? '#dc2626' : 'var(--text-muted)'
                    }}>
                    ✕ {ar ? 'لم يُدفع بعد' : 'טרם שולם'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── خطوة 2: اختيار المزارعين والأراضي ── */}
          {step === 2 && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={ar ? '🔍 بحث عن مزارع...' : '🔍 חיפוש חקלאי...'} style={{ flex: 1 }} />
                <button type="button" className="btn btn-outline btn-sm" onClick={selectAllVisible}>
                  {ar ? 'تحديد الكل' : 'בחר הכל'}
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={clearAll}>
                  {ar ? 'إلغاء التحديد' : 'נקה בחירה'}
                </button>
              </div>

              <div style={{ border: '1.5px solid var(--border)', borderRadius: 10, maxHeight: 380, overflowY: 'auto' }}>
                {/* رأس الجدول */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '26px 90px 1fr 100px',
                  gap: 8, padding: '7px 12px', background: '#f5f3ff',
                  fontSize: 11, fontWeight: 800, color: '#5b21b6',
                  position: 'sticky', top: 0, zIndex: 1, borderBottom: '1px solid #ddd6fe',
                }}>
                  <span></span>
                  <span style={{ textAlign: 'center' }}>{ar ? 'المحطة' : 'עמדה'}</span>
                  <span>{ar ? 'الأرض / المنطقة' : 'קרקע / אזור'}</span>
                  <span style={{ textAlign: 'center' }}>{ar ? `كوب ${currentYear}` : `קוב ${currentYear}`}</span>
                </div>

                {filteredFarmers.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    {ar ? 'لا توجد نتائج' : 'אין תוצאות'}
                  </div>
                )}

                {filteredFarmers.map(({ farmer, farmerLands }) => {
                  const allChecked = farmerLands.every(l => selected[`${l.farmerId}_${l.id}`]);
                  const someChecked = farmerLands.some(l => selected[`${l.farmerId}_${l.id}`]);
                  return (
                    <div key={farmer.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      {/* رأس المزارع — تحديد كل أراضيه دفعة واحدة */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '9px 12px', background: someChecked ? '#f5f3ff' : '#fafafa',
                      }}>
                        <input type="checkbox" checked={allChecked}
                          ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                          onChange={e => toggleFarmerAll(farmerLands, e.target.checked)}
                          style={cbStyle} />
                        <span style={{ fontWeight: 800, fontSize: 13, fontFamily: 'Heebo,sans-serif', flex: 1 }}>
                          {farmer.nameHeb || farmer.name}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {farmerLands.length} {ar ? 'أرض' : 'קרקעות'}
                        </span>
                      </div>

                      {/* أراضي المزارع — كل أرض بصف مستقل */}
                      {farmerLands.map(l => {
                        const key = `${l.farmerId}_${l.id}`;
                        const checked = !!selected[key];
                        const cups = cupsThisYear(l.farmerId, l.id);
                        return (
                          <label key={l.id} onClick={() => toggleLand(key)} style={{
                            display: 'grid', gridTemplateColumns: '26px 90px 1fr 100px',
                            gap: 8, alignItems: 'center',
                            padding: '7px 12px 7px 30px', cursor: 'pointer',
                            background: checked ? '#f5f3ff' : '#fff',
                            borderTop: '1px solid #f8f8f8',
                          }}>
                            <input type="checkbox" checked={checked} readOnly style={cbStyle} />
                            <code style={{
                              background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '2px 6px',
                              borderRadius: 5, fontWeight: 900, fontSize: 12, textAlign: 'center',
                            }}>{l.stationNumber || '—'}</code>
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Heebo,sans-serif' }}>
                              {landLabel(l)}
                            </span>
                            <span style={{
                              fontSize: 11, fontWeight: 700, textAlign: 'center',
                              color: cups != null ? '#16a34a' : 'var(--border)',
                            }}>
                              {cups != null ? `🪣 ${Math.round(cups).toLocaleString()}` : '—'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 700, color: '#5b21b6' }}>
                {selectedCount} {ar ? 'أرض محددة' : 'קרקעות נבחרו'}
              </div>
            </div>
          )}

          {/* ── خطوة 3: معاينة قبل التطبيق ── */}
          {step === 3 && (
            <div>
              {result ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>{result.fail === 0 ? '✅' : '⚠️'}</div>
                  <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
                    {ar
                      ? `تم تطبيق الإضافة على ${result.success} أرض بنجاح`
                      : `התוספת הוחלה על ${result.success} קרקעות בהצלחה`}
                  </div>
                  {result.fail > 0 && (
                    <div style={{ color: '#dc2626', fontSize: 13, fontWeight: 700 }}>
                      {ar ? `فشل تطبيقها على ${result.fail} أرض` : `נכשל עבור ${result.fail} קרקעות`}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                    {ar ? `سيتم تطبيق "${title.trim()}" (₪${amount}) — ` : `תוחל "${title.trim()}" (₪${amount}) — `}
                    <strong style={{ color: '#5b21b6' }}>{applyRows.length}</strong> {ar ? 'أرض' : 'קרקעות'}
                  </div>

                  <div style={{ border: '1.5px solid var(--border)', borderRadius: 10, maxHeight: 320, overflowY: 'auto' }}>
                    {applyRows.length === 0 && (
                      <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                        {ar ? 'لا توجد أراضٍ محدَّدة' : 'אין קרקעות שנבחרו'}
                      </div>
                    )}
                    {applyRows.map(row => (
                      <div key={row.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: 13 }}>
                          <span style={{ fontWeight: 700, fontFamily: 'Heebo,sans-serif' }}>{farmerName(row.farmerId)}</span>
                          <span style={{ color: 'var(--text-muted)' }}> — {row.land?.stationNumber || '—'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 6, color: 'var(--text-muted)',
                            background: 'var(--surface-2)',
                            textDecoration: paidFully ? 'line-through' : 'none',
                          }}>
                            {title.trim()} — ₪{amount}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {paidFully ? (ar ? 'مدفوع' : 'שולם') : (ar ? 'لم يُدفع' : 'לא שולם')}
                          </span>
                          <button type="button" onClick={() => setExcluded(prev => ({ ...prev, [row.key]: true }))}
                            style={{ width: 22, height: 22, borderRadius: 6, border: '1.5px solid #fca5a5', background: '#fff1f2', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {applying && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ height: 6, borderRadius: 3, background: '#ede9fe', overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#7c3aed', width: `${Math.round(progress / Math.max(1, applyRows.length) * 100)}%`, transition: 'width 0.2s' }} />
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
                        {progress} / {applyRows.length}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* الفوتر */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-outline" disabled={step === 1 || applying}
            onClick={() => setStep(s => Math.max(1, s - 1))} style={{ visibility: step === 1 ? 'hidden' : 'visible' }}>
            {ar ? 'رجوع' : 'חזור'}
          </button>
          {result ? (
            <button type="button" className="btn btn-primary" onClick={onClose}>
              {ar ? 'إغلاق' : 'סגור'}
            </button>
          ) : step < 3 ? (
            <button type="button" className="btn btn-primary"
              disabled={(step === 1 && (!title.trim() || !amount)) || (step === 2 && selectedCount === 0)}
              onClick={() => setStep(s => Math.min(3, s + 1))}>
              {ar ? 'التالي' : 'הבא'}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={applying || applyRows.length === 0}
              onClick={applyBulk} style={{ background: '#7c3aed', borderColor: '#7c3aed' }}>
              {applying ? `⏳ ${ar ? 'جاري التطبيق...' : 'מחיל...'}` : `✓ ${ar ? 'تطبيق نهائي' : 'החלה סופית'} (${applyRows.length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  PenaltyModal — تطبيق "غرامة تأخير" تلقائياً على الأراضي الغير مدفوعة
//  ✅ الفكرة: لكل أرض مختارة، نحسب الأكواب الغير مدفوعة حالياً (بكل سنواتها)
//  × سعر لكل كوب (يُقترح من prices.latePenaltyRate، قابل للتعديل هالمرة بس)،
//  وننشئ إضافة (LandExtra) عادية بـkind:'penalty' بهالمبلغ. هيك الغرامة
//  بتنعكس تلقائياً بكل مكان بالتطبيق (تقارير، لوحة مزارع، كشف واتساب...)
//  بدون أي تعديل على أي حساب موجود — وإلغاؤها = حذف هالإضافة بالذات (بنفس
//  زر ✕ العادي) فيرجع المبلغ لطبيعته بالضبط.
//  ✅ أي أرض عندها غرامة سابقة لسا موجودة (ما انحذفت) — ما تظهر أصلاً
//  بقائمة الاختيار، منعاً لتكرار الغرامة فوق بعضها.
// ════════════════════════════════════════════════════════════
function PenaltyModal({ farmers, lands, regions, readings, landExtrasByLand, prices, onClose, onApplied, ar }) {
  const [step, setStep] = useState(1);
  const [rate, setRate] = useState(String(prices?.latePenaltyRate ?? 0.5));
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({}); // key: farmerId_landId -> true
  const [excluded, setExcluded] = useState({}); // key -> true (أُزيل يدوياً من المعاينة)
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null); // {success, fail}

  // ✅ لتفادي إغلاق النافذة عند السحب (تحديد نص) من الداخل للخارج
  const backdropMouseDown = useRef(false);

  const cbStyle = { width: 16, height: 16, minWidth: 16, flexShrink: 0, accentColor: '#dc2626', cursor: 'pointer' };

  const farmerName = id => {
    const f = farmers.find(x => String(x.id) === String(id));
    if (!f) return '—';
    return `${f.lastName || ''} ${f.firstName || ''}`.trim() || f.nameHeb || f.name || '—';
  };

  const landLabel = (land) => {
    if (!land) return '—';
    if (land.regionId) {
      const reg = regions.find(r => String(r.id) === String(land.regionId));
      if (reg?.nameHeb && reg.nameHeb !== reg.name) return reg.nameHeb;
      if (reg?.name) return reg.name;
    }
    return land.description || '—';
  };

  // ✅ عندها غرامة نشطة أصلاً؟ (kind='penalty' موجودة، بغض النظر عن حالة دفعها — المعيار
  // هو الحذف مو الدفع؛ طالما ما انحذفت فهي "لسا موجودة")
  const hasActivePenalty = (landId) => (landExtrasByLand[String(landId)] || []).some(e => e.kind === 'penalty');

  // ✅ إجمالي الأكواب الغير مدفوعة حالياً لهذه الأرض — عبر كل قراءاتها/سنواتها، فترة
  // فترة حسب paidPeriods (نفس تعريف "الفترة النشطة" المستخدم بكل مكان تاني بالتطبيق)
  const cupsUnpaid = (farmerId, landId) => {
    const rs = readings.filter(x => String(x.farmerId) === String(farmerId) && String(x.landId) === String(landId));
    let total = 0;
    rs.forEach(r => {
      const vals = r.readings || [];
      const changes = r.meterChanges || [];
      const periodsCount = Math.max(0, vals.length - 1);
      for (let i = 0; i < periodsCount; i++) {
        const active = vals[i] != null && vals[i] !== '';
        if (!active) continue;
        if (r.paidPeriods && r.paidPeriods[i]) continue; // مدفوعة — ما تُحسب
        const c = cupsPositive(vals, i, changes);
        if (c && c > 0) total += c;
      }
    });
    return total;
  };

  // ✅ فقط الأراضي: (أ) عندها أكواب غير مدفوعة، و(ب) ما عندها غرامة سابقة لسا موجودة
  const farmersWithLands = farmers
    .map(f => ({
      farmer: f,
      farmerLands: lands
        .filter(l => String(l.farmerId) === String(f.id))
        .filter(l => !hasActivePenalty(l.id))
        .map(l => ({ ...l, unpaidCups: cupsUnpaid(l.farmerId, l.id) }))
        .filter(l => l.unpaidCups > 0),
    }))
    .filter(x => x.farmerLands.length > 0)
    .sort((a, b) => (a.farmer.nameHeb || a.farmer.name || '').localeCompare(b.farmer.nameHeb || b.farmer.name || '', 'ar'));

  const filteredFarmers = farmersWithLands.filter(x => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (x.farmer.nameHeb || x.farmer.name || '').toLowerCase().includes(q);
  });

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleLand = (key) => setSelected(prev => ({ ...prev, [key]: !prev[key] }));

  const toggleFarmerAll = (farmerLands, checked) => {
    setSelected(prev => {
      const next = { ...prev };
      farmerLands.forEach(l => { next[`${l.farmerId}_${l.id}`] = checked; });
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(prev => {
      const next = { ...prev };
      filteredFarmers.forEach(({ farmerLands }) => {
        farmerLands.forEach(l => { next[`${l.farmerId}_${l.id}`] = true; });
      });
      return next;
    });
  };

  const clearAll = () => setSelected({});

  const rateNum = parseFloat(rate) || 0;

  const applyRows = Object.keys(selected).filter(k => selected[k] && !excluded[k]).map(key => {
    const [farmerId, landId] = key.split('_');
    const land = lands.find(l => String(l.id) === String(landId));
    const unpaidCups = cupsUnpaid(farmerId, landId);
    // ✅ نفس طريقة برنامج المحاسبة بالضبط: السعر لكل كوب يُدخل **قبل الضريبة** (مثل 0.42)،
    // سطر صافٍ مقرّب لأغورة (أكواب × سعر)، وبعدين تُضاف الضريبة (מע"מ) ويُقرّب لأغورة.
    const net = Math.round(unpaidCups * rateNum * 100) / 100;
    const amount = Math.round(net * (1 + getVatRate(prices)) * 100) / 100;
    return { key, farmerId, landId, land, unpaidCups, amount };
  });

  const applyPenalties = async () => {
    if (applyRows.length === 0 || rateNum <= 0) return;
    if (!window.confirm(
      ar
        ? `تطبيق غرامة تأخير (₪${rateNum}/كوب) على ${applyRows.length} أرض؟`
        : `להחיל קנס איחור (₪${rateNum}/קוב) על ${applyRows.length} קרקעות?`
    )) return;
    setApplying(true); setProgress(0);
    let success = 0, fail = 0;
    for (const row of applyRows) {
      try {
        const noteTxt = ar
          ? `⏰ غرامة تأخير — ${Math.round(row.unpaidCups).toLocaleString()} كوب غير مدفوع × ₪${rateNum}`
          : `⏰ קנס איחור — ${Math.round(row.unpaidCups).toLocaleString()} קוב שלא שולם × ₪${rateNum}`;
        await adminAPI.createLandExtra({
          landId: row.landId,
          note: noteTxt,
          amount: row.amount,
          paid: 0,
          kind: 'penalty',
        });
        success++;
      } catch (e) { console.error(e); fail++; }
      setProgress(p => p + 1);
    }
    setApplying(false);
    setResult({ success, fail });
    onApplied();
  };

  return (
    <div
      onMouseDown={(e) => { backdropMouseDown.current = (e.target === e.currentTarget); }}
      onClick={(e) => {
        if (backdropMouseDown.current && e.target === e.currentTarget && !applying) onClose();
        backdropMouseDown.current = false;
      }}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>

        {/* الرأس */}
        <div style={{ padding: '14px 20px', background: '#7f1d1d', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>⏰</span>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>
              {ar ? 'تطبيق غرامة تأخير' : 'החלת קנס איחור'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#fecaca', fontSize: 12 }}>{ar ? `الخطوة ${step} من 3` : `שלב ${step} מתוך 3`}</span>
            {!applying && (
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 26, height: 26, borderRadius: '50%', cursor: 'pointer' }}>✕</button>
            )}
          </div>
        </div>

        {/* شريط التقدّم */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0' }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{ height: 4, flex: 1, borderRadius: 4, background: n <= step ? '#dc2626' : '#e5e7eb' }} />
          ))}
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>

          {/* ── خطوة 1: السعر لكل كوب ── */}
          {step === 1 && (
            <div>
              <div className="form-group">
                <label>₪ {ar ? 'السعر لكل كوب غير مدفوع (قبل الضريبة) *' : 'מחיר לכל קוב שלא שולם (לפני מע"מ) *'}</label>
                <input type="number" min="0" step="any" value={rate} onChange={e => setRate(e.target.value)}
                  placeholder="0.5" style={{ width: '100%', fontWeight: 700 }} />
                <small style={{ color: 'var(--text-muted)' }}>
                  {ar
                    ? 'معبّى تلقائياً من الإعدادات — تقدر تغيّره هلق لهالمرة بس، بدون ما يغيّر الإعداد الافتراضي.'
                    : 'מולא אוטומטית מההגדרות — ניתן לשנות עכשיו חד-פעמית, מבלי לשנות את ברירת המחדל.'}
                </small>
              </div>
              <div style={{ fontSize: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', color: '#991b1b', fontWeight: 600 }}>
                ⚠️ {ar
                  ? 'الغرامة بتُحسب على كل الأكواب الغير مدفوعة حالياً لكل أرض تختارها بالخطوة الجاية، وبتُضاف كإضافة منفصلة تقدر تحذفها بأي وقت لترجيع السعر الطبيعي.'
                  : 'הקנס יחושב על כל הקוב שלא שולם כרגע לכל חלקה שתבחר בשלב הבא, ויתווסף כתוספת נפרדת שניתן למחוק בכל עת כדי לחזור למחיר הרגיל.'}
              </div>
            </div>
          )}

          {/* ── خطوة 2: اختيار الأراضي الغير مدفوعة ── */}
          {step === 2 && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={ar ? '🔍 بحث عن مزارع...' : '🔍 חיפוש חקלאי...'} style={{ flex: 1 }} />
                <button type="button" className="btn btn-outline btn-sm" onClick={selectAllVisible}>
                  {ar ? 'تحديد الكل' : 'בחר הכל'}
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={clearAll}>
                  {ar ? 'إلغاء التحديد' : 'נקה בחירה'}
                </button>
              </div>

              <div style={{ border: '1.5px solid var(--border)', borderRadius: 10, maxHeight: 380, overflowY: 'auto' }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '26px 90px 1fr 110px',
                  gap: 8, padding: '7px 12px', background: '#fef2f2',
                  fontSize: 11, fontWeight: 800, color: '#991b1b',
                  position: 'sticky', top: 0, zIndex: 1, borderBottom: '1px solid #fecaca',
                }}>
                  <span></span>
                  <span style={{ textAlign: 'center' }}>{ar ? 'المحطة' : 'עמדה'}</span>
                  <span>{ar ? 'الأرض / المنطقة' : 'קרקע / אזור'}</span>
                  <span style={{ textAlign: 'center' }}>{ar ? 'كوب غير مدفوع' : 'קוב לא שולם'}</span>
                </div>

                {filteredFarmers.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    {ar ? 'لا توجد أراضٍ غير مدفوعة (أو كلها عندها غرامة مطبّقة أصلاً)' : 'אין קרקעות שלא שולמו (או שכולן כבר עם קנס)'}
                  </div>
                )}

                {filteredFarmers.map(({ farmer, farmerLands }) => {
                  const allChecked = farmerLands.every(l => selected[`${l.farmerId}_${l.id}`]);
                  const someChecked = farmerLands.some(l => selected[`${l.farmerId}_${l.id}`]);
                  return (
                    <div key={farmer.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '9px 12px', background: someChecked ? '#fef2f2' : '#fafafa',
                      }}>
                        <input type="checkbox" checked={allChecked}
                          ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                          onChange={e => toggleFarmerAll(farmerLands, e.target.checked)}
                          style={cbStyle} />
                        <span style={{ fontWeight: 800, fontSize: 13, fontFamily: 'Heebo,sans-serif', flex: 1 }}>
                          {farmer.nameHeb || farmer.name}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {farmerLands.length} {ar ? 'أرض' : 'קרקעות'}
                        </span>
                      </div>

                      {farmerLands.map(l => {
                        const key = `${l.farmerId}_${l.id}`;
                        const checked = !!selected[key];
                        return (
                          <label key={l.id} onClick={() => toggleLand(key)} style={{
                            display: 'grid', gridTemplateColumns: '26px 90px 1fr 110px',
                            gap: 8, alignItems: 'center',
                            padding: '7px 12px 7px 30px', cursor: 'pointer',
                            background: checked ? '#fef2f2' : '#fff',
                            borderTop: '1px solid #f8f8f8',
                          }}>
                            <input type="checkbox" checked={checked} readOnly style={cbStyle} />
                            <code style={{
                              background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '2px 6px',
                              borderRadius: 5, fontWeight: 900, fontSize: 12, textAlign: 'center',
                            }}>{l.stationNumber || '—'}</code>
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Heebo,sans-serif' }}>
                              {landLabel(l)}
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, textAlign: 'center', color: '#dc2626' }}>
                              🪣 {Math.round(l.unpaidCups).toLocaleString()}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              <div style={{ marginTop: 10, fontSize: 13, fontWeight: 700, color: '#991b1b' }}>
                {selectedCount} {ar ? 'أرض محددة' : 'קרקעות נבחרו'}
              </div>
            </div>
          )}

          {/* ── خطوة 3: معاينة قبل التطبيق ── */}
          {step === 3 && (
            <div>
              {result ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>{result.fail === 0 ? '✅' : '⚠️'}</div>
                  <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
                    {ar
                      ? `تم تطبيق الغرامة على ${result.success} أرض بنجاح`
                      : `הקנס הוחל על ${result.success} קרקעות בהצלחה`}
                  </div>
                  {result.fail > 0 && (
                    <div style={{ color: '#dc2626', fontSize: 13, fontWeight: 700 }}>
                      {ar ? `فشل تطبيقها على ${result.fail} أرض` : `נכשל עבור ${result.fail} קרקעות`}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                    {ar ? `سيتم تطبيق غرامة (₪${rateNum}/كوب) — ` : `יוחל קנס (₪${rateNum}/קוב) — `}
                    <strong style={{ color: '#991b1b' }}>{applyRows.length}</strong> {ar ? 'أرض' : 'קרקעות'}
                  </div>

                  <div style={{ border: '1.5px solid var(--border)', borderRadius: 10, maxHeight: 320, overflowY: 'auto' }}>
                    {applyRows.length === 0 && (
                      <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                        {ar ? 'لا توجد أراضٍ محدَّدة' : 'אין קרקעות שנבחרו'}
                      </div>
                    )}
                    {applyRows.map(row => (
                      <div key={row.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: 13 }}>
                          <span style={{ fontWeight: 700, fontFamily: 'Heebo,sans-serif' }}>{farmerName(row.farmerId)}</span>
                          <span style={{ color: 'var(--text-muted)' }}> — {row.land?.stationNumber || '—'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            🪣 {Math.round(row.unpaidCups).toLocaleString()}
                          </span>
                          <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, fontWeight: 800, color: '#991b1b', background: '#fef2f2' }}>
                            ₪{row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <button type="button" onClick={() => setExcluded(prev => ({ ...prev, [row.key]: true }))}
                            style={{ width: 22, height: 22, borderRadius: 6, border: '1.5px solid #fca5a5', background: '#fff1f2', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {applying && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ height: 6, borderRadius: 3, background: '#fee2e2', overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#dc2626', width: `${Math.round(progress / Math.max(1, applyRows.length) * 100)}%`, transition: 'width 0.2s' }} />
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
                        {progress} / {applyRows.length}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* الفوتر */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-outline" disabled={step === 1 || applying}
            onClick={() => setStep(s => Math.max(1, s - 1))} style={{ visibility: step === 1 ? 'hidden' : 'visible' }}>
            {ar ? 'رجوع' : 'חזור'}
          </button>
          {result ? (
            <button type="button" className="btn btn-primary" onClick={onClose}>
              {ar ? 'إغلاق' : 'סגור'}
            </button>
          ) : step < 3 ? (
            <button type="button" className="btn btn-primary"
              disabled={(step === 1 && rateNum <= 0) || (step === 2 && selectedCount === 0)}
              onClick={() => setStep(s => Math.min(3, s + 1))}
              style={{ background: '#dc2626', borderColor: '#dc2626' }}>
              {ar ? 'التالي' : 'הבא'}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={applying || applyRows.length === 0}
              onClick={applyPenalties} style={{ background: '#dc2626', borderColor: '#dc2626' }}>
              {applying ? `⏳ ${ar ? 'جاري التطبيق...' : 'מחיל...'}` : `✓ ${ar ? 'تطبيق نهائي' : 'החלה סופית'} (${applyRows.length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// initialFarmerId — اختياري: تفتح الصفحة مفلترة على مزارع (من كشف حساب المزارع)
export default function AdminReadings({ adminRole = 'admin', initialFarmerId = '' }) {
  const isViewer = adminRole === 'viewer';
  const { lang } = useLang();
  const ar = lang === 'ar';

  const [farmers, setFarmers] = useState([]);
  const [lands, setLands] = useState([]);
  const [regions, setRegions] = useState([]);
  const [readings, setReadings] = useState([]);
  const [prices, setPrices] = useState({ globalPrice: 0, yearPrices: {}, landPrices: {} });
  // ✅ مشاريع اللجنة — تُستخدم فقط لحساب المزارعين المتأخرين بالدفع (تحذير ⚠️ بجدول القراءات)
  const [projects, setProjects] = useState([]);
  // ✅ إضافات الأراضي (LandExtra) — مصفوفة مسطّحة لكل الإضافات بالنظام، تُجمَّع حسب landId عند الحاجة
  const [landExtras, setLandExtras] = useState([]);
  // ✅ مخزن الإضافات (ExtraCatalogItem) — أسماء/أسعار جاهزة تُقترح عند إضافة LandExtra جديدة
  const [extraCatalog, setExtraCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showRForm, setShowRForm] = useState(false);
  const [editR, setEditR] = useState(null);
  const rFormBackdropMouseDown = useRef(false); // ✅ لتفادي إغلاق المودال عند السحب (تحديد نص) من الداخل للخارج
  const [rForm, setRForm] = useState(EMPTY_FORM);

  const [filterF, setFilterF] = useState(initialFarmerId || '');
  const [filterY, setFilterY] = useState('');
  const [filterR, setFilterR] = useState('');
  // ✅ فلترة حسب رقم محطة محدد (مثلاً K7) — نص مطابَق مباشرة مع reading.stationNumber
  const [filterStation, setFilterStation] = useState('');
  const [filterPaid, setFilterPaid] = useState('');
  const [farmerSearch, setFarmerSearch] = useState('');
  const [showFarmerList, setShowFarmerList] = useState(false);
  // ✅ بحث المزارع في النموذج
  const [formFarmerSearch, setFormFarmerSearch] = useState('');
  const [showFormFarmerList, setShowFormFarmerList] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // ✅ شباك الإضافة الجماعية
  const [showBulkModal, setShowBulkModal] = useState(false);
  // ✅ شباك تطبيق غرامة التأخير
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fd, ld, rd, rg, pr, pd, ed, ec] = await Promise.all([
        adminAPI.getFarmers(),
        adminAPI.getLands(),
        adminAPI.getReadings(),
        regionsAPI.getRegions(),
        adminAPI.getPrices(),
        adminAPI.getProjects(),
        adminAPI.getLandExtras(), // ✅ كل إضافات الأراضي
        adminAPI.getExtraCatalog(), // ✅ مخزن الإضافات
      ]);
      setFarmers(fd.farmers || []);
      setLands(ld.lands || []);
      setReadings(rd.readings || []);
      setRegions(rg.regions || []);
      setPrices(pr || { globalPrice: 0, yearPrices: {}, landPrices: {} });
      setProjects(pd.projects || []);
      setLandExtras(ed.extras || []);
      setExtraCatalog(ec.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ✅ خريطة landId -> [إضافات] — تُبنى من المصفوفة المسطّحة عند كل تغيير
  const landExtrasByLand = groupExtrasByLand(landExtras);

  // ✅ إعادة تحميل الإضافات فقط (بعد إضافة/تعديل/حذف إضافة واحدة) — أخف من load() الكامل
  const reloadLandExtras = async () => {
    try { const ed = await adminAPI.getLandExtras(); setLandExtras(ed.extras || []); }
    catch (e) { setError(e.message); }
  };

  // ✅ إعادة تحميل مخزن الإضافات فقط (بعد تحديث سعر عنصر من داخل صف إضافة)
  const reloadExtraCatalog = async () => {
    try { const ec = await adminAPI.getExtraCatalog(); setExtraCatalog(ec.items || []); }
    catch (e) { setError(e.message); }
  };

  // ✅ تحديث سعر عنصر بمخزن الإضافات بشكل دائم — يُستدعى من زر "تحديث سعر المخزن" بـExtraRow
  const updateCatalogPrice = async (itemId, newPrice) => {
    try {
      await adminAPI.updateExtraCatalogItem(itemId, { defaultPrice: newPrice });
      await reloadExtraCatalog();
    } catch (e) { setError(e.message); }
  };

  const addLandExtraForCurrentLand = async () => {
    if (!rForm.landId) return;
    try {
      await adminAPI.createLandExtra({ landId: rForm.landId, note: '', amount: 0, paid: 0 });
      await reloadLandExtras();
    } catch (e) { setError(e.message); }
  };

  const saveLandExtra = async (extraId, patch) => {
    try {
      await adminAPI.updateLandExtra(extraId, patch);
      await reloadLandExtras();
    } catch (e) { setError(e.message); }
  };

  const removeLandExtra = async (extraId) => {
    if (!window.confirm(ar ? 'حذف هذه الإضافة نهائياً؟' : 'למחוק תוספת זו לצמיתות?')) return;
    try {
      await adminAPI.deleteLandExtra(extraId);
      await reloadLandExtras();
    } catch (e) { setError(e.message); }
  };

  const farmerName = id => {
    const f = farmers.find(x => String(x.id) === String(id));
    if (!f) return '—';
    return `${f.lastName || ''} ${f.firstName || ''}`.trim() || f.nameHeb || f.name || '—';
  };

  const landName = id => {
    const land = lands.find(l => String(l.id) === String(id));
    if (!land) return '—';
    if (land.regionId) {
      const reg = regions.find(r => String(r.id) === String(land.regionId));
      if (reg?.nameHeb && reg.nameHeb !== reg.name) return reg.nameHeb;
      if (reg?.name) return reg.name;
    }
    const code = land.stationNumber?.match(/^([A-Za-z]+)/)?.[1]?.toUpperCase();
    if (code) {
      const reg = regions.find(r => r.name?.toUpperCase() === code);
      if (reg?.nameHeb && reg.nameHeb !== reg.name) return reg.nameHeb;
      if (reg?.name) return reg.name;
    }
    return land.stationNumber || '—';
  };

  const regionName = id => id ? (regions.find(r => String(r.id) === String(id))?.name || '') : '';
  const landRegion = lid => {
    const l = lands.find(x => String(x.id) === String(lid));
    return l?.regionId ? regionName(l.regionId) : '';
  };
  const years = [...new Set(readings.map(r => r.year))].sort((a, b) => b - a);
  const farmerLands = rForm.farmerId ? lands.filter(l => String(l.farmerId) === String(rForm.farmerId)) : lands;

  // ✅ قائمة محطات بدون تكرار — لفلتر "المحطة" أسفل. نفس منطق التنظيف المستخدم بصفحتي
  // المزارعين والمشاريع: نجمع الأراضي حسب رقم المحطة (نص) ونمثّل كل مجموعة بسجل واحد،
  // بدل عرض نفس رقم المحطة عدة مرات (لو أكثر من مزارع/ساعة مسجّلين عليها).
  const uniqueStations = (() => {
    const groups = {};
    lands.filter(l => l.stationNumber).forEach(l => {
      const key = l.stationNumber.trim();
      if (!groups[key]) groups[key] = l;
    });
    return Object.values(groups).sort((a, b) => (a.stationNumber || '').localeCompare(b.stationNumber || ''));
  })();

  // ✅ مزارعون لم يكملوا دفعهم لمشروع/مشاريع معينة — Map: farmerId -> [{ projectName, remaining, stationNumber }]
  // ✅ stationNumber هنا (نص) هو مفتاح المطابقة الفعلي مع reading.stationNumber، وهذا يتجاوز
  // مشكلة الأراضي المكررة بقاعدة البيانات كلياً. الأولوية: محطة المشترك نفسه (m.stationNumber —
  // لمشاريع تخص عدة محطات مختلفة كل مشترك بمحطته، مثل "تطوير طريق")، وإلا محطة المشروع العامة
  // (p.stationNumber — لمشروع يخص محطة واحدة بالذات)، وإلا فارغ = توافق قديم يشمل كل أراضي المزارع.
  const unpaidProjectsByFarmer = (() => {
    const map = {};
    projects.forEach(p => {
      (p.members || []).forEach(m => {
        if (m.amount === null || m.amount === undefined) return;
        const paid = (m.payments || []).reduce((s, pay) => s + (parseFloat(pay.amount) || 0), 0);
        const remaining = m.amount - paid;
        if (remaining > 0.01) {
          if (!map[m.farmerId]) map[m.farmerId] = [];
          const stationNumber = (m.stationNumber && m.stationNumber.trim()) || p.stationNumber || '';
          map[m.farmerId].push({ projectName: p.name, remaining, stationNumber });
        }
      });
    });
    return map;
  })();

  const openAddR = () => {
    setEditR(null);
    setRForm(EMPTY_FORM);
    setFormFarmerSearch('');
    setError(''); setShowRForm(true);
  };

  const openEditR = r => {
    setEditR(r);
    // ✅ نضع اسم المزارع في حقل البحث
    const f = farmers.find(x => String(x.id) === String(r.farmerId));
    setFormFarmerSearch(f ? (f.nameHeb || f.name || '') : '');
    setRForm({
      farmerId: r.farmerId, landId: r.landId, year: r.year,
      readings: [...r.readings.map(String)],
      // ✅ تحميل تبديلات العداد المخزّنة مسبقاً (كنصوص قابلة للتعديل بالحقول)
      meterChanges: (r.meterChanges || []).map(m => ({
        period: m.period,
        oldFinal: String(m.oldFinal ?? ''),
        newInitial: String(m.newInitial ?? ''),
      })),
    });
    setError(''); setShowRForm(true);
  };

  const submitR = async e => {
    e.preventDefault();
    if (!rForm.farmerId || !rForm.landId) { setError(ar ? 'اختر المزارع والأرض' : 'בחר חקלאי וקרקע'); return; }
    if (!rForm.readings[0] && rForm.readings[0] !== 0) { setError(ar ? "القراءة الأولى مطلوبة" : "קריאה ראשונה חובה"); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...rForm,
        meterChanges: (rForm.meterChanges || []).map(m => ({
          period: m.period,
          oldFinal: parseFloat(m.oldFinal),
          newInitial: parseFloat(m.newInitial),
        })).filter(m => !isNaN(m.oldFinal) && !isNaN(m.newInitial)), // ✅ يتجاهل التبديلات غير المكتملة
      };
      if (editR) await adminAPI.updateReading(editR.id, payload);
      else await adminAPI.createReading(payload);
      setShowRForm(false); load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const delR = async id => {
    if (!window.confirm(ar ? 'حذف هذه القراءة؟' : 'למחוק קריאה זו?')) return;
    await adminAPI.deleteReading(id); load();
  };

  const addReadingField = () => setRForm({ ...rForm, readings: [...rForm.readings, ''] });
  const removeReadingField = i => {
    if (rForm.readings.length <= 2) return;
    // ✅ عند حذف حقل قراءة، لازم نصحح فهارس تبديلات العداد كي تبقى متوافقة مع الفهرسة الجديدة
    const removedPeriod = i - 1; // الفترة المرتبطة بهذا الحقل (إن وُجدت)
    setRForm({
      ...rForm,
      readings: rForm.readings.filter((_, idx) => idx !== i),
      meterChanges: (rForm.meterChanges || [])
        .filter(m => m.period !== removedPeriod)
        .map(m => m.period > removedPeriod ? { ...m, period: m.period - 1 } : m),
    });
  };
  const updateReadingField = (i, v) => {
    const r = [...rForm.readings]; r[i] = v; setRForm({ ...rForm, readings: r });
  };

  // ✅ يبحث عن تبديل عداد مسجّل لفترة معينة ضمن النموذج الحالي
  const getMeterChangeFor = (period) => (rForm.meterChanges || []).find(m => m.period === period);

  // ✅ تفعيل/إلغاء تبديل عداد لفترة معينة
  const toggleMeterChange = (period) => {
    setRForm(prev => {
      const existing = (prev.meterChanges || []).find(m => m.period === period);
      if (existing) {
        return { ...prev, meterChanges: prev.meterChanges.filter(m => m.period !== period) };
      }
      return { ...prev, meterChanges: [...(prev.meterChanges || []), { period, oldFinal: '', newInitial: '' }] };
    });
  };

  // ✅ تحديث حقل (oldFinal أو newInitial) لتبديل عداد معين
  const updateMeterChangeField = (period, field, value) => {
    setRForm(prev => ({
      ...prev,
      meterChanges: (prev.meterChanges || []).map(m => m.period === period ? { ...m, [field]: value } : m),
    }));
  };

  const filtered = readings.filter(r => {
    if (filterF && String(r.farmerId) !== String(filterF)) return false;
    if (filterY && r.year !== parseInt(filterY)) return false;
    if (filterR) {
      const land = lands.find(l => String(l.id) === String(r.landId));
      if (!land || String(land.regionId) !== String(filterR)) return false;
    }
    // ✅ فلترة حسب رقم محطة محدد — مطابقة نصية مباشرة مع reading.stationNumber
    // (نفس مبدأ المطابقة المعتمد بكل مكان آخر بالمشروع؛ يتجاوز مشكلة الأراضي المكررة)
    if (filterStation && String(r.stationNumber || '').trim() !== filterStation) return false;
    // ✅ فلتر الدفع — فترة فترة (paidPeriods)، بنفس تعريف "الفترة النشطة" المستخدم
    // بالضبط بدالة getPayStatus في ReadingsTable.js (وجود قراءة بداية vals[i] فقط —
    // بدون شرط أن يكون الاستهلاك محسوباً >0). هيك الفلتر بيطابق تماماً شارات ✅/⚠️/❌
    // فوق الجدول، وتظهر القراءات "دفع جزئي" ضمن "مدفوع" (فيها فترات مدفوعة) وضمن
    // "غير مدفوع" (فيها فترات لسا) بدل ما تختفي بالكامل من الفلترتين.
    if (filterPaid === 'paid' || filterPaid === 'unpaid') {
      const vals = r.readings || [];
      const periodsCount = Math.max(0, vals.length - 1);
      let hasPaid = false, hasUnpaid = false;
      for (let i = 0; i < periodsCount; i++) {
        if (vals[i] == null || vals[i] === '') continue; // فترة لم تبدأ بعد — مش "نشطة"
        if (r.paidPeriods && r.paidPeriods[i]) hasPaid = true;
        else hasUnpaid = true;
      }
      if (filterPaid === 'paid' && !hasPaid) return false;
      if (filterPaid === 'unpaid' && !hasUnpaid) return false;
    }
    return true;
  });

  // ✅ إجمالي المبالغ (قبل / بعد الضريبة) للقراءات المعروضة حالياً حسب الفلاتر
  // (تبديل العداد يُدمج داخل نفس رقم فترته تلقائياً عبر cupsPositive — لا حاجة لأي معالجة إضافية هنا)
  // ✅ عند فلتر "مدفوع فقط" / "غير مدفوع" — نحسب فقط الفترات المطابقة لحالة الدفع المطلوبة
  // (مو كل فترات القراءة)، حتى تتناغم هاي الأرقام تماماً مع شريط "نسبة الدفع" بالأسفل.
  const grandBeforeVat = filtered.reduce((sum, r) => {
    const vals = r.readings || [];
    const changes = r.meterChanges || [];
    return sum + vals.slice(1).reduce((s, _, i) => {
      const cups = cupsPositive(vals, i, changes);
      if (cups <= 0) return s;
      const isPaid = !!(r.paidPeriods && r.paidPeriods[i]);
      if (filterPaid === 'paid' && !isPaid) return s;
      if (filterPaid === 'unpaid' && isPaid) return s;
      return s + cups * getBasePrice(prices, r.year, r.landId, i + 1);
    }, 0);
  }, 0);
  const grandAfterVat = filtered.reduce((sum, r) => {
    const vals = r.readings || [];
    const changes = r.meterChanges || [];
    return sum + vals.slice(1).reduce((s, _, i) => {
      const cups = cupsPositive(vals, i, changes);
      if (cups <= 0) return s;
      const isPaid = !!(r.paidPeriods && r.paidPeriods[i]);
      if (filterPaid === 'paid' && !isPaid) return s;
      if (filterPaid === 'unpaid' && isPaid) return s;
      return s + cups * getPrice(prices, r.year, r.landId, i + 1);
    }, 0);
  }, 0);
  const vatPercentLabel = (getVatRate(prices) * 100).toFixed(1).replace(/\.0$/, '');

  // ✅ نسبة الدفع (₪) — أكواب فقط (بدون إضافات)، بنفس منطق ✅/⚠️/❌ بجدول القراءات:
  // فترة فترة حسب paidPeriods (وليس r.paid العام للقراءة كلها، لأنه بيضخّم النسبة عند الدفع الجزئي)
  // ✅ total هنا دايماً بيشمل كل فترات القراءات المفلترة (مدفوعة وغير مدفوعة) — عكس
  // grandBeforeVat/grandAfterVat يلي بيتقيّدوا بحالة الدفع تبع الفلتر، عشان الشريط يفضل
  // يعكس نسبة حقيقية (مش 100% دايماً لما يكون الفلتر "مدفوع فقط").
  const paymentProgress = (() => {
    let total = 0, paid = 0;
    filtered.forEach(r => {
      const vals = r.readings || [];
      const changes = r.meterChanges || [];
      vals.slice(1).forEach((_, i) => {
        const cups = cupsPositive(vals, i, changes);
        if (cups <= 0) return;
        const amt = cups * getPrice(prices, r.year, r.landId, i + 1);
        total += amt;
        if (r.paidPeriods && r.paidPeriods[i]) paid += amt;
      });
    });
    const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
    return { total, paid, pct };
  })();

  // ✅ إضافات: الكامل مقابل المدفوع — للأراضي التي تظهر قراءاتها حالياً بالجدول (حسب الفلاتر)
  // ⚠️ مهم: الإضافات تابعة للأرض وليست للقراءة — إذا كان لنفس الأرض عدة قراءات (سنوات) ظاهرة
  // بالجدول حالياً، لازم نحسب إضافات تلك الأرض "مرة واحدة فقط" مش مرة لكل سنة (تفادياً لمضاعفة
  // المبلغ المطلوب فعلياً)
  const filteredLandIds = [...new Set(filtered.map(r => String(r.landId)))];
  const extrasForFilteredLands = filteredLandIds.flatMap(lid => landExtrasByLand[lid] || []);
  const extrasGrossTotal = getExtrasGross(extrasForFilteredLands);
  const extrasPaidTotal = extrasGrossTotal - getExtrasNet(extrasForFilteredLands);

  // ✅ مشاريع: مجموع (الكامل + المدفوع) لكل مزارع — Map: farmerId → { required, paid }
  const projectsTotalsByFarmer = (() => {
    const map = {};
    projects.forEach(p => {
      (p.members || []).forEach(m => {
        if (!m.farmerId) return;
        if (m.amount === null || m.amount === undefined) return;
        const paid = (m.payments || []).reduce((s, pay) => s + (parseFloat(pay.amount) || 0), 0);
        if (!map[m.farmerId]) map[m.farmerId] = { required: 0, paid: 0 };
        map[m.farmerId].required += m.amount;
        map[m.farmerId].paid += paid;
      });
    });
    return map;
  })();

  // ✅ فقط للمزارعين الظاهرين حالياً بالجدول (حسب الفلاتر)، بدون تكرار
  const projectsTotals = (() => {
    const farmerIdsShown = new Set(filtered.map(r => r.farmerId));
    let required = 0, paid = 0;
    farmerIdsShown.forEach(fid => {
      const tt = projectsTotalsByFarmer[fid];
      if (tt) { required += tt.required; paid += tt.paid; }
    });
    return { required, paid };
  })();

  // ✅ الإجمالي الشامل: مياه + إضافات + مشاريع — المدفوع مقابل الكامل
  const combinedTotal = paymentProgress.total + extrasGrossTotal + projectsTotals.required;
  const combinedPaid = paymentProgress.paid + extrasPaidTotal + projectsTotals.paid;
  const combinedPct = combinedTotal > 0 ? Math.min(100, Math.round((combinedPaid / combinedTotal) * 100)) : 0;
  // ✅ إجمالي الأكواب + تفصيل كل دورة (قراءة) للقراءات المعروضة حالياً
  const cupsBreakdown = (() => {
    const byPeriod = {};
    let total = 0, maxPeriods = 0;
    filtered.forEach(r => {
      const vals = r.readings || [];
      const changes = r.meterChanges || [];
      vals.slice(1).forEach((_, i) => {
        const cups = cupsPositive(vals, i, changes);
        if (cups) { byPeriod[i + 1] = (byPeriod[i + 1] || 0) + cups; total += cups; }
        maxPeriods = Math.max(maxPeriods, i + 1);
      });
    });
    return { total, byPeriod, maxPeriods };
  })();

  // ✅ إجمالي مبلغ النموذج — يدمج تبديل العداد ضمن نفس رقم فترته (بدون انزياح بالسعر)
  const formTotalAmount = rForm.readings.slice(1).reduce((total, _, i) => {
    const change = getMeterChangeFor(i);
    const prev = parseFloat(rForm.readings[i]);
    const curr = parseFloat(rForm.readings[i + 1]);
    let cups = 0;
    if (!isNaN(prev) && !isNaN(curr)) {
      if (change) {
        const of = parseFloat(change.oldFinal), ni = parseFloat(change.newInitial);
        cups = (!isNaN(of) && !isNaN(ni)) ? (of - prev) + (curr - ni) : 0;
      } else {
        cups = curr - prev;
      }
    }
    const price = getPrice(prices, rForm.year, rForm.landId, i + 1);
    return total + (cups > 0 ? cups * price : 0);
  }, 0);

  // ✅ إضافات الأرض المختارة حالياً بالنموذج (تابعة لـ landId، بغض النظر عن السنة)
  const currentLandExtras = rForm.landId ? (landExtrasByLand[rForm.landId] || []) : [];
  // إجمالي الإضافات المتبقية لهذه الأرض
  const extrasTotal = getExtrasNet(currentLandExtras);

  // ✅ تحويل رقم الهاتف لصيغة دولية يفهمها رابط واتساب (wa.me) — افتراض إسرائيل (972+)
  const normalizePhone = (phone) => {
    let digits = (phone || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('0')) digits = '972' + digits.slice(1);
    else if (!digits.startsWith('972')) digits = '972' + digits;
    return digits;
  };

  // ✅ يبني نص كشف واتساب لمزارع واحد: تفصيل كل أرض/فترة + الإضافات غير المدفوعة (مرة واحدة
  // لكل أرض، مش لكل سنة) + المبلغ الإجمالي غير المدفوع
  const buildFarmerStatement = (farmer, readingsForFarmer) => {
    let grandTotal = 0;
    const landBlocks = [];
    const extrasShownForLand = new Set(); // ✅ نمنع تكرار إضافات نفس الأرض لو عندها أكثر من قراءة/سنة بالكشف

    readingsForFarmer.forEach(r => {
      const land = lands.find(l => String(l.id) === String(r.landId));
      const stationLabel = land?.stationNumber || '';
      // ✅ اسم الأرض الوصفي (منطقة/اسم خاص) — يُعرض بجانب رقم المحطة لأن بعض
      // المزارعين لا يعرفون رقم محطتهم، بينما اسم الأرض/المنطقة مألوف لهم
      const landDisplay = landName(r.landId);
      const landLabelStr = (stationLabel && landDisplay && landDisplay !== stationLabel)
        ? `${stationLabel} — ${landDisplay}`
        : (stationLabel || landDisplay || '');

      const vals = r.readings || [];
      const changes = r.meterChanges || [];
      const periodLines = [];
      let landTotal = 0;

      vals.slice(1).forEach((_, i) => {
        const cups = cupsPositive(vals, i, changes);
        if (cups <= 0) return;
        const price = getPrice(prices, r.year, r.landId, i + 1);
        const amt = cups * price;
        const isPaid = !!(r.paidPeriods && r.paidPeriods[i]);
        periodLines.push(
          `   - ${ar ? 'فترة' : 'תקופה'} ${i + 1}: ${Math.round(cups).toLocaleString()} ${ar ? 'كوب' : 'קוב'} — ₪${Math.round(amt).toLocaleString()} ${isPaid ? (ar ? '[مدفوع]' : '[שולם]') : (ar ? '[غير مدفوع]' : '[לא שולם]')}`
        );
        if (!isPaid) { landTotal += amt; grandTotal += amt; }
      });

      // ✅ الإضافات غير المدفوعة لهذه الأرض — مرة واحدة بس، حتى لو ظهرت لهذا المزارع أكثر
      // من قراءة/سنة لنفس الأرض بهذا الكشف
      if (!extrasShownForLand.has(String(r.landId))) {
        extrasShownForLand.add(String(r.landId));
        (landExtrasByLand[r.landId] || []).forEach(ex => {
          const amt = parseFloat(ex.amount) || 0;
          const paidAmt = parseFloat(ex.paid) || 0;
          const rem = amt - paidAmt;
          if (rem > 0.01) {
            periodLines.push(`   - ${ex.note || (ar ? 'إضافة' : 'תוספת')}: ₪${Math.round(rem).toLocaleString()} ${ar ? '[غير مدفوع]' : '[לא שולם]'}`);
            landTotal += rem; grandTotal += rem;
          }
        });
      }

      if (periodLines.length > 0) {
        landBlocks.push(
          `${ar ? 'الأرض' : 'קרקע'}: ${landLabelStr} (${r.year})\n${periodLines.join('\n')}` +
          (landTotal > 0 ? `\n   ${ar ? 'مجموع الأرض' : 'סה"כ קרקע'}: ₪${Math.round(landTotal).toLocaleString()}` : '')
        );
      }
    });

    const farmerDisplayName = farmer.nameHeb || farmer.name || '';
    const header = `${ar ? 'مرحباً' : 'שלום'} ${farmerDisplayName}،\n${ar ? 'كشف قراءة المياه — الشلالة' : 'דו"ח קריאת מים — השלאלה'}\n`;
    const body = landBlocks.length > 0
      ? landBlocks.join('\n\n')
      : (ar ? 'لا يوجد مبلغ مستحق حالياً' : 'אין סכום לתשלום כרגע');
    const footer = grandTotal > 0
      ? `\n\n${ar ? 'الإجمالي المطلوب دفعه' : 'סה"כ לתשלום'}: ₪${Math.round(grandTotal).toLocaleString()}`
      : '';

    return `${header}\n${body}${footer}`;
  };

  // ✅ زر واتساب: يعمل فقط عند الفلترة على مزارع واحد بالتحديد (لأن الرسالة مخصصة لشخص واحد)
  const sendWhatsAppStatement = () => {
    const farmerIds = [...new Set(filtered.map(r => String(r.farmerId)))];
    if (farmerIds.length !== 1) {
      alert(ar ? 'فلتر باسم مزارع واحد فقط أولاً من الأعلى' : 'סנן קודם לפי חקלאי אחד בלבד');
      return;
    }
    const farmer = farmers.find(f => String(f.id) === farmerIds[0]);
    if (!farmer) return;
    if (!farmer.phone) {
      alert(ar ? 'لا يوجد رقم هاتف مسجل لهذا المزارع' : 'אין מספר טלפון רשום לחקלאי זה');
      return;
    }
    const text = buildFarmerStatement(farmer, filtered);
    const digits = normalizePhone(farmer.phone);
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div>
      {/* ══ شباك الإضافة الجماعية ══ */}
      {showBulkModal && (
        <BulkExtraModal
          farmers={farmers}
          lands={lands}
          regions={regions}
          readings={readings}
          ar={ar}
          onClose={() => setShowBulkModal(false)}
          onApplied={() => { load(); }}
        />
      )}

      {/* ══ شباك تطبيق غرامة التأخير ══ */}
      {showPenaltyModal && (
        <PenaltyModal
          farmers={farmers}
          lands={lands}
          regions={regions}
          readings={readings}
          landExtrasByLand={landExtrasByLand}
          prices={prices}
          ar={ar}
          onClose={() => setShowPenaltyModal(false)}
          onApplied={() => { load(); }}
        />
      )}

      {/* ✅ ترويسة طباعة أنيقة — تظهر فقط عند الطباعة (مخفية على الشاشة) */}
      <div className="print-letterhead">
        <div className="print-letterhead-brand">🌿 الشلالة — نظام إدارة مياه الري</div>
        <div className="print-letterhead-title">{ar ? 'كشف قراءة مياه' : 'דו"ח קריאת מים'}</div>
        {filtered.length === 1 && (
          <div className="print-letterhead-info">
            <span>{ar ? 'المزارع' : 'חקלאי'}: <strong>{farmerName(filtered[0].farmerId)}</strong></span>
            <span>{ar ? 'المحطة' : 'עמדה'}: <strong>{filtered[0].stationNumber || landName(filtered[0].landId)}</strong></span>
            <span>{ar ? 'السنة' : 'שנה'}: <strong>{filtered[0].year}</strong></span>
          </div>
        )}
        <div className="print-letterhead-date">
          {new Date().toLocaleDateString(ar ? 'ar-EG-u-nu-latn' : 'he-IL')}
        </div>
      </div>

      {/* ── ملخّص الحساب: قبل وبعد الضريبة (מע"מ) ── */}
      <div className="flex-gap gap-12 mb-16 print-summary" style={{ flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px', background: 'var(--surface-2)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '10px 16px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 2 }}>
            {ar ? 'الإجمالي قبل الضريبة' : 'סה"כ לפני מע"מ'}
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--text-secondary)' }}>
            ₪{Math.round(grandBeforeVat).toLocaleString()}
          </div>
        </div>
        <div style={{ flex: '1 1 200px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 10, padding: '10px 16px' }}>
          <div style={{ fontSize: 12, color: '#92400e', fontWeight: 700, marginBottom: 2 }}>
            🧾 {ar ? `الإجمالي بعد الضريبة (${vatPercentLabel}%)` : `סה"כ אחרי מע"מ (${vatPercentLabel}%)`}
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#854d0e' }}>
            ₪{Math.round(grandAfterVat).toLocaleString()}
          </div>
        </div>
      </div>
      {/* ✅ سطر جديد: الإجمالي الشامل (مياه + إضافات + مشاريع) */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-secondary)' }}>
          💰 {ar ? 'الإجمالي الشامل (مياه + إضافات + مشاريع)' : 'סה"כ כולל (מים + תוספות + פרויקטים)'}:
        </span>{' '}
        <span style={{ fontSize: 14, fontWeight: 900, color: combinedPct >= 100 ? '#16a34a' : '#dc2626' }}>
          {combinedPct}% (₪{Math.round(combinedPaid).toLocaleString()} / ₪{Math.round(combinedTotal).toLocaleString()})
        </span>
      </div>

      {/* ── شريط نسبة الدفع ── */}
      <div className="card mb-16" style={{ padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-secondary)' }}>
            💳 {ar ? 'نسبة الدفع' : 'אחוז תשלום'}
          </span>
          <span style={{ fontSize: 13, fontWeight: 900, color: paymentProgress.pct >= 100 ? '#16a34a' : '#dc2626' }}>
            {paymentProgress.pct}% (₪{Math.round(paymentProgress.paid).toLocaleString()} / ₪{Math.round(paymentProgress.total).toLocaleString()})
          </span>
        </div>
        <div style={{ height: 10, borderRadius: 6, background: '#fee2e2', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 6,
            width: `${paymentProgress.pct}%`,
            background: paymentProgress.pct >= 100 ? '#16a34a' : 'linear-gradient(90deg,#16a34a,#22c55e)',
            transition: 'width 0.6s ease',
          }} />
        </div>
      </div>

      {/* ── إجمالي الأكواب + تفصيل الدورات ── */}
      <div className="card mb-16 print-cups-card">
        <div className="flex-between mb-12" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0 }}>
            🥤 {ar ? 'إجمالي الأكواب' : 'סה"כ קובים'}
            {filterY && <span style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: 14 }}> — {filterY}</span>}
            {filterStation && <span style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: 14, fontFamily: 'monospace' }}> — 📍 {filterStation}</span>}
          </h3>
          <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--primary)' }}>
            {Math.round(cupsBreakdown.total).toLocaleString()}
          </div>
        </div>
        {cupsBreakdown.maxPeriods === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '8px 0' }}>
            {ar ? 'لا توجد بيانات كافية لعرض التفصيل' : 'אין מספיק נתונים להצגת פירוט'}
          </p>
        ) : (
          <div className="print-cups-detail" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {Array.from({ length: cupsBreakdown.maxPeriods }, (_, idx) => idx + 1).map(p => {
              const val = cupsBreakdown.byPeriod[p] || 0;
              const pct = cupsBreakdown.total > 0 ? Math.round((val / cupsBreakdown.total) * 100) : 0;
              return (
                <div key={p} style={{ flex: '1 1 130px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 4 }}>
                    {ar ? `الدورة ${p}` : `תקופה ${p}`}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--primary-dark)', marginBottom: 6 }}>
                    {Math.round(val).toLocaleString()}
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: '#e5e7eb', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: 'var(--primary)', borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, textAlign: 'left' }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── شريط الفلاتر ──
          ✅ شبكة موحّدة (readings-filters-grid معرّفة بـ global.css): على الشاشات الكبيرة
          يظهر بحث المزارع بجانب بقية العناصر بصف واحد، وعلى الموبايل يمتد بحث المزارع
          بعرض كامل بصف مستقل وتتوزّع الأربعة الباقية بشبكة عمودين تحته. */}
      <div className="mb-16">
        <div className="readings-filters-grid" style={{ marginBottom: 12 }}>
          {/* بحث المزارع */}
          <div className="readings-farmer-search" style={{ position: 'relative' }}>
            <input type="text" value={farmerSearch}
              onChange={e => { setFarmerSearch(e.target.value); setShowFarmerList(true); }}
              onFocus={() => setShowFarmerList(true)}
              onBlur={() => setTimeout(() => setShowFarmerList(false), 150)}
              placeholder={filterF
                ? (farmers.find(f => f.id === filterF)?.nameHeb || farmers.find(f => f.id === filterF)?.name || '')
                : (ar ? '🔍 اختر مزارعاً...' : '🔍 חפש חקלאי...')}
              style={{ width: '100%', paddingLeft: 8 }} />
            {filterF && (
              <button onClick={() => { setFilterF(''); setFarmerSearch(''); }}
                style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1 }}>✕</button>
            )}
            {showFarmerList && (
              <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, background: '#fff', border: '1.5px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto', minWidth: 220, width: '100%' }}>
                <div onMouseDown={() => { setFilterF(''); setFarmerSearch(''); setShowFarmerList(false); }}
                  style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  {ar ? '— الكل —' : '— הכל —'}
                </div>
                {farmers.filter(f => { const q = farmerSearch.toLowerCase(); return !q || (f.nameHeb || f.name || '').toLowerCase().includes(q); })
                  .map(f => (
                    <div key={f.id} onMouseDown={() => { setFilterF(f.id); setFarmerSearch(''); setShowFarmerList(false); }}
                      style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'Heebo,sans-serif', fontWeight: 600, background: filterF === f.id ? '#f0fdf4' : '' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                      onMouseLeave={e => e.currentTarget.style.background = filterF === f.id ? '#f0fdf4' : ''}>
                      {f.nameHeb || f.name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <select value={filterR} onChange={e => setFilterR(e.target.value)} style={{ width: '100%' }}>
            <option value="">{t('allRegions', lang)}</option>
            {regions.map(r => (
              <option key={r.id} value={r.id}>{r.name}{r.nameHeb && r.nameHeb !== r.name ? ` — ${r.nameHeb}` : ''}</option>
            ))}
          </select>
          {/* ✅ فلتر محطة محددة (مثلاً K7) — يعرض فقط قراءات هذه المحطة بالذات */}
          <select value={filterStation} onChange={e => setFilterStation(e.target.value)}
            style={{ width: '100%', fontFamily: 'monospace', fontWeight: 700 }}>
            <option value="">{ar ? '— كل المحطات —' : '— כל התחנות —'}</option>
            {uniqueStations.map(l => (
              <option key={l.id} value={l.stationNumber}>📍 {l.stationNumber}</option>
            ))}
          </select>
          <select value={filterY} onChange={e => setFilterY(e.target.value)} style={{ width: '100%' }}>
            <option value="">{t('allYears', lang)}</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={filterPaid} onChange={e => setFilterPaid(e.target.value)} style={{ width: '100%' }}>
            <option value="">{ar ? 'الكل' : 'הכל'}</option>
            <option value="paid">{ar ? '✅ مدفوع فقط' : '✅ שולם בלבד'}</option>
            <option value="unpaid">{ar ? '❌ غير مدفوع' : '❌ לא שולם'}</option>
          </select>
        </div>
        {/* ✅ صف أزرار الإجراءات — صف أفقي بمنتصف الشاشة على الحاسوب، وشبكة 2×2 متناسقة على الموبايل
            (readings-actions-row معرّفة بـ global.css) */}
        <div className="readings-actions-row">
          {/* ✅ موحّدة الحجم والشكل مع بقية أزرار الصف (نفس فئة btn وارتفاع/حشو موحّد) */}
          <button className="btn btn-outline" onClick={() => window.print()}>
            🖨️ {ar ? 'طباعة' : 'הדפסה'}
          </button>
          <button className="btn" onClick={sendWhatsAppStatement}
            title={ar ? 'إرسال كشف واتساب للمزارع (فلتر بمزارع واحد أولاً)' : 'שלח דו"ח בוואטסאפ (סנן חקלאי אחד קודם)'}
            style={{ background: '#25D366', color: '#fff', border: '1.5px solid #25D366', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <svg viewBox="0 0 32 32" width="16" height="16" fill="currentColor" aria-hidden="true">
              <path d="M16.004 3C9.377 3 4 8.373 4 15c0 2.24.615 4.42 1.78 6.32L4 29l7.86-1.75A11.94 11.94 0 0 0 16.004 27C22.63 27 28 21.627 28 15S22.63 3 16.004 3Zm0 21.8c-1.93 0-3.82-.52-5.47-1.5l-.39-.23-4.66 1.04 1.02-4.54-.25-.4A9.77 9.77 0 0 1 5.2 15c0-5.96 4.85-10.8 10.8-10.8 5.96 0 10.8 4.84 10.8 10.8 0 5.96-4.84 10.8-10.8 10.8Zm5.93-8.1c-.32-.16-1.9-.94-2.2-1.04-.3-.11-.51-.16-.73.16-.21.32-.84 1.04-1.03 1.25-.19.21-.38.24-.7.08-.32-.16-1.35-.5-2.57-1.6-.95-.85-1.6-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.14-.14.32-.38.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.73-1.76-1-2.41-.26-.63-.53-.55-.73-.56-.19-.01-.4-.01-.62-.01-.21 0-.56.08-.86.4-.3.32-1.13 1.1-1.13 2.7 0 1.6 1.16 3.14 1.32 3.36.16.21 2.28 3.48 5.53 4.88.77.33 1.37.53 1.84.68.77.24 1.47.21 2.02.13.62-.09 1.9-.78 2.17-1.53.27-.75.27-1.4.19-1.53-.08-.13-.29-.21-.61-.37Z" />
            </svg>
            {ar ? 'واتساب' : 'וואטסאפ'}
          </button>
          {!isViewer && (
            <button className="btn" style={{ background: '#4c1d95', color: '#fff', border: '1.5px solid #4c1d95' }}
              onClick={() => setShowBulkModal(true)}>
              👥 {ar ? 'إضافة جماعية' : 'הוספה קבוצתית'}
            </button>
          )}
          {!isViewer && (
            <button className="btn" style={{ background: '#dc2626', color: '#fff', border: '1.5px solid #dc2626' }}
              onClick={() => setShowPenaltyModal(true)}>
              ⏰ {ar ? 'غرامة تأخير' : 'קנס איחור'}
            </button>
          )}
          {!isViewer && <button className="btn btn-primary" onClick={openAddR}>+ {ar ? 'إضافة قراءة' : 'הוסף קריאה'}</button>}
        </div>
      </div>

      {error && <div className="alert alert-error mb-16">{error}</div>}

      {/* ══ نموذج القراءة — مودال مركّز بمنتصف الشاشة بدل بطاقة تدفع الصفحة لفوق ══ */}
      {showRForm && (
        <div
          onMouseDown={(e) => { rFormBackdropMouseDown.current = (e.target === e.currentTarget); }}
          onClick={(e) => {
            if (rFormBackdropMouseDown.current && e.target === e.currentTarget) setShowRForm(false);
            rFormBackdropMouseDown.current = false;
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} className="fade-in-fast"
            style={{ background: '#fff', borderRadius: 20, border: '2px solid var(--primary)', padding: '20px 24px', width: '100%', maxWidth: 820, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
            <div className="flex-between mb-16">
              <h3 style={{ margin: 0 }}>
                {editR ? `✏️ ${ar ? 'تعديل قراءة' : 'עריכת קריאה'}` : `+ ${ar ? 'إضافة قراءة' : 'הוסף קריאה'}`}
              </h3>
              <button type="button" onClick={() => setShowRForm(false)}
                style={{ background: 'var(--surface-2)', border: 'none', color: 'var(--text-muted)', width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <form onSubmit={submitR}>
              <div className="grid-3">
                <div className="form-group" style={{ position: 'relative' }}>
                  <label>{ar ? 'المزارع *' : 'חקלאי *'}</label>
                  <input
                    value={formFarmerSearch}
                    onChange={e => { setFormFarmerSearch(e.target.value); setShowFormFarmerList(true); }}
                    onFocus={() => setShowFormFarmerList(true)}
                    onBlur={() => setTimeout(() => setShowFormFarmerList(false), 150)}
                    placeholder={ar ? '🔍 ابحث باسم المزارع...' : '🔍 חפש חקלאי...'}
                    style={{ width: '100%', borderColor: !rForm.farmerId && formFarmerSearch ? '#ef4444' : '' }}
                    autoComplete="off"
                  />
                  {/* اسم المزارع المختار */}
                  {rForm.farmerId && (
                    <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, marginTop: 3 }}>
                      ✓ {farmers.find(f => f.id === rForm.farmerId)?.nameHeb || farmers.find(f => f.id === rForm.farmerId)?.name || ''}
                    </div>
                  )}
                  {/* قائمة البحث */}
                  {showFormFarmerList && (
                    <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200, background: '#fff', border: '1.5px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto', minWidth: '100%', width: 'max-content' }}>
                      {farmers
                        .filter(f => {
                          const q = formFarmerSearch.toLowerCase();
                          return !q || (f.nameHeb || f.name || '').toLowerCase().includes(q) || (f.lastName || '').toLowerCase().includes(q) || (f.firstName || '').toLowerCase().includes(q);
                        })
                        .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '', 'ar'))
                        .map(f => (
                          <div key={f.id}
                            onMouseDown={() => {
                              setRForm(prev => ({ ...prev, farmerId: f.id, landId: '' }));
                              setFormFarmerSearch(f.nameHeb || f.name || '');
                              setShowFormFarmerList(false);
                            }}
                            style={{ padding: '9px 14px', cursor: 'pointer', fontFamily: 'Heebo,sans-serif', fontWeight: 600, fontSize: 13, background: rForm.farmerId === f.id ? '#f0fdf4' : '', borderBottom: '1px solid #f3f4f6' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background = rForm.farmerId === f.id ? '#f0fdf4' : ''}>
                            {f.nameHeb || f.name}
                            {f.phone && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 8 }}>{f.phone}</span>}
                          </div>
                        ))}
                      {farmers.filter(f => { const q = formFarmerSearch.toLowerCase(); return !q || (f.nameHeb || f.name || '').toLowerCase().includes(q) || (f.lastName || '').toLowerCase().includes(q); }).length === 0 && (
                        <div style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
                          {ar ? 'لا توجد نتائج' : 'אין תוצאות'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>{ar ? 'المحطة *' : 'תחנה *'}</label>
                  <select value={rForm.landId} onChange={e => setRForm({ ...rForm, landId: e.target.value })} disabled={!rForm.farmerId}>
                    <option value="">{rForm.farmerId ? `— ${ar ? 'اختر محطة' : 'בחר תחנה'} —` : `— ${ar ? 'اختر المزارع أولاً' : 'בחר חקלאי תחילה'} —`}</option>
                    {farmerLands.map(l => (
                      <option key={l.id} value={l.id}>
                        {(() => {
                          const reg = regions.find(r => String(r.id) === String(l.regionId));
                          const regName = reg?.nameHeb && reg.nameHeb !== reg.name ? reg.nameHeb : reg?.name || '';
                          return `${l.stationNumber}${regName ? ` — ${regName}` : ''}`;
                        })()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('year', lang)} *</label>
                  <input type="number" value={rForm.year} onChange={e => setRForm({ ...rForm, year: e.target.value })} min={2000} max={2100} />
                </div>
              </div>

              {/* القراءات */}
              <div className="form-group">
                <label>{ar ? 'القراءات' : 'קריאות'}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rForm.readings.map((v, i) => {
                    const periodIndex = i - 1; // ✅ الفترة المرتبطة بهذا الحقل (بين القراءة i-1 والقراءة i)
                    const change = i > 0 ? getMeterChangeFor(periodIndex) : null;
                    const prev = parseFloat(rForm.readings[i - 1]);
                    const curr = parseFloat(v);
                    let cups = null;
                    if (i > 0 && v !== '' && !isNaN(prev) && !isNaN(curr)) {
                      if (change) {
                        const of = parseFloat(change.oldFinal), ni = parseFloat(change.newInitial);
                        cups = (!isNaN(of) && !isNaN(ni)) ? (of - prev) + (curr - ni) : null;
                      } else {
                        cups = curr - prev;
                      }
                    }
                    const price = i > 0 ? getPrice(prices, rForm.year, rForm.landId, i) : 0;
                    const amount = cups !== null && cups > 0 ? cups * price : null;
                    const isEmpty = v === '' || v === null;
                    return (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ width: 130, fontSize: 13, fontWeight: 700, color: i === 0 ? 'var(--primary)' : 'var(--text-muted)', flexShrink: 0 }}>
                            {ar ? 'قراءة' : 'קריאה'} {i + 1}
                            {i === 0 ? ` (${ar ? 'بداية *' : 'התחלה *'})` : ` (${ar ? 'فترة' : 'תקופה'} ${i})`}
                          </span>
                          <input type="number" step="any" value={v}
                            onChange={e => updateReadingField(i, e.target.value)}
                            placeholder={i === 0 ? (ar ? 'مطلوب' : 'חובה') : (ar ? 'لم تؤخذ بعد' : 'טרם נלקחה')}
                            style={{ width: 130, fontWeight: 700, borderColor: i === 0 && isEmpty ? '#ef4444' : (change ? '#a855f7' : '') }} />

                          {/* ✅ زر تبديل العداد — يظهر لكل فترة (من الحقل الثاني وما فوق) */}
                          {i > 0 && (
                            <button type="button" onClick={() => toggleMeterChange(periodIndex)}
                              title={ar ? 'فعّلها إذا صار تبديل/تعطّل للعداد أثناء هذه الفترة' : 'הפעל אם הוחלף המונה בתקופה זו'}
                              style={{
                                fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, cursor: 'pointer',
                                border: change ? '1.5px solid #a855f7' : '1.5px solid var(--border)',
                                background: change ? '#f3e8ff' : '#fff',
                                color: change ? '#7c3aed' : 'var(--text-muted)',
                              }}>
                              🔄 {change ? (ar ? 'تبديل عداد بهذه الفترة ✓' : 'החלפת מונה בתקופה זו ✓') : (ar ? 'تبديل عداد بهذه الفترة؟' : 'החלפת מונה בתקופה זו?')}
                            </button>
                          )}

                          {cups !== null && (
                            <span style={{ fontSize: 12, fontWeight: 700, minWidth: 90, color: cups >= 0 ? '#16a34a' : '#dc2626', background: cups >= 0 ? '#f0fdf4' : '#fff1f2', border: `1px solid ${cups >= 0 ? '#bbf7d0' : '#fca5a5'}`, padding: '2px 10px', borderRadius: 6 }}>
                              {cups >= 0 ? `🪣 ${cups}` : `⚠️ ${cups}`} {ar ? 'م³' : 'קוב'}
                            </span>
                          )}
                          {amount !== null && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#854d0e', background: '#fef9c3', border: '1px solid #fde047', padding: '2px 10px', borderRadius: 6 }}>
                              💰 ₪{Math.round(amount).toLocaleString()}
                            </span>
                          )}
                          {i >= 2 && (
                            <button type="button" onClick={() => removeReadingField(i)}
                              style={{ width: 26, height: 26, borderRadius: 6, border: '1.5px solid #fca5a5', background: '#fff1f2', color: '#dc2626', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>✕</button>
                          )}
                        </div>

                        {/* ✅ حقول تبديل العداد — تظهر فقط عند تفعيل الزر لهذه الفترة */}
                        {change && (
                          <div style={{ marginRight: 140, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', background: '#faf5ff', border: '1px dashed #d8b4fe', borderRadius: 10, padding: '10px 12px' }}>
                            <div>
                              <label style={{ fontSize: 11, color: '#7c3aed', fontWeight: 700, display: 'block', marginBottom: 3 }}>
                                {ar ? 'آخر قراءة على العداد القديم' : 'קריאה אחרונה במונה הישן'}
                              </label>
                              <input type="number" step="any" value={change.oldFinal}
                                onChange={e => updateMeterChangeField(periodIndex, 'oldFinal', e.target.value)}
                                placeholder={ar ? 'مثال: 14620' : 'לדוג׳: 14620'}
                                style={{ width: 140, fontWeight: 700 }} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, color: '#7c3aed', fontWeight: 700, display: 'block', marginBottom: 3 }}>
                                {ar ? 'أول قراءة على العداد الجديد' : 'קריאה ראשונה במונה החדש'}
                              </label>
                              <input type="number" step="any" value={change.newInitial}
                                onChange={e => updateMeterChangeField(periodIndex, 'newInitial', e.target.value)}
                                placeholder={ar ? 'مثال: 199 أو 0' : 'לדוג׳: 199 או 0'}
                                style={{ width: 140, fontWeight: 700 }} />
                            </div>
                            <div style={{ fontSize: 11, color: '#7c3aed', flex: '1 1 200px' }}>
                              💡 {ar
                                ? `الاستهلاك هذه الفترة = (إغلاق القديم − ${isNaN(prev) ? '؟' : prev}) + (${isNaN(curr) ? '؟' : curr} − بداية الجديد) — ويُحسب بسعر نفس هذه الفترة (فترة ${i}) بدون أي تغيير بترقيمها.`
                                : `הצריכה בתקופה זו = (סגירת הישן − ${isNaN(prev) ? '?' : prev}) + (${isNaN(curr) ? '?' : curr} − פתיחת החדש) — ותחושב לפי מחיר אותה תקופה (תקופה ${i}).`}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button type="button" onClick={addReadingField} className="btn btn-outline btn-sm" style={{ width: 'fit-content', marginTop: 4 }}>
                    + {ar ? 'إضافة فترة' : 'הוסף תקופה'}
                  </button>
                </div>
                {formTotalAmount > 0 && (
                  <div style={{ marginTop: 14, background: 'linear-gradient(135deg,#14532d,#166534)', borderRadius: 10, padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#a3e635', fontWeight: 700, fontSize: 13 }}>💰 {ar ? 'مجموع الأكواب:' : 'סה"כ קובים:'}</span>
                    <span style={{ color: '#fde68a', fontWeight: 900, fontSize: 20 }}>₪{Math.round(formTotalAmount).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* ✅ قسم الإضافات — الآن تابعة للأرض المختارة (landId) وتُحفظ فوراً بالسيرفر،
                  بغض النظر عن سنة القراءة المفتوحة هلق. بتظهر بنفس الشكل لو فتحت أي قراءة
                  تانية لنفس الأرض (سنة مختلفة)، لحد ما تُعلَّم مدفوعة بالكامل. */}
              <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <h4 style={{ margin: 0, fontSize: 14, color: '#92400e' }}>
                    ➕ {ar ? 'إضافات هذه الأرض' : 'תוספות הקרקע הזו'}
                    {currentLandExtras.length > 0 && (
                      <span style={{ marginRight: 8, background: '#fed7aa', color: '#92400e', borderRadius: 8, padding: '1px 8px', fontSize: 12 }}>
                        {currentLandExtras.length}
                      </span>
                    )}
                  </h4>
                  <button type="button" onClick={addLandExtraForCurrentLand} disabled={!rForm.landId}
                    style={{ padding: '5px 14px', borderRadius: 8, border: '1.5px solid #f59e0b', background: '#fff', color: '#92400e', cursor: rForm.landId ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 12, opacity: rForm.landId ? 1 : 0.5 }}>
                    + {ar ? 'إضافة جديدة' : 'תוספת חדשה'}
                  </button>
                </div>

                {!rForm.landId ? (
                  <div style={{ textAlign: 'center', color: '#d97706', fontSize: 12, padding: '8px 0' }}>
                    {ar ? 'اختر المزارع والمحطة أولاً' : 'בחר חקלאי ותחנה תחילה'}
                  </div>
                ) : currentLandExtras.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#d97706', fontSize: 12, padding: '8px 0' }}>
                    {ar ? 'لا توجد إضافات على هذه الأرض — اضغط + لإضافة' : 'אין תוספות לקרקע זו — לחץ + להוסיף'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {currentLandExtras.map(ex => (
                      <ExtraRow key={ex.id} extra={ex}
                        onSave={patch => saveLandExtra(ex.id, patch)}
                        onRemove={() => removeLandExtra(ex.id)}
                        catalog={extraCatalog}
                        prices={prices}
                        onUpdateCatalogPrice={updateCatalogPrice}
                        ar={ar}
                      />
                    ))}
                    {/* إجمالي الإضافات */}
                    {currentLandExtras.length > 1 && (
                      <div style={{ background: '#92400e', borderRadius: 8, padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#fde68a', fontWeight: 700, fontSize: 13 }}>
                          {ar ? 'إجمالي الإضافات المتبقية على هذه الأرض:' : 'סה"כ תוספות שנותרו לקרקע זו:'}
                        </span>
                        <span style={{ color: '#fff', fontWeight: 900, fontSize: 18 }}>₪{extrasTotal.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              <div className="flex-gap gap-12">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? t('saving', lang) : `💾 ${t('save', lang)}`}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setShowRForm(false)}>
                  {t('cancel', lang)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══ جدول القراءات ══ */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
      ) : (
        <ReadingsTable
          readings={filtered}
          setReadings={setReadings}
          farmerName={farmerName}
          landName={landName}
          landRegion={landRegion}
          onEdit={openEditR}
          onDelete={delR}
          lang={lang}
          prices={prices}
          isViewer={isViewer}
          lands={lands}
          regions={regions}
          unpaidProjectsByFarmer={unpaidProjectsByFarmer}
          landExtrasByLand={landExtrasByLand}
        />
      )}
    </div>
  );
}